import { useMemo } from 'react'
import { api } from '@/api/client'
import { API_BASE_URL } from '@/api/baseUrl'
import { useToast } from '@/hooks/useToast'
import { errorMessage } from '@/lib/apiError'
import { isNativeApp } from '@/mobile/platform'

/**
 * THE one way the SPA downloads, opens, previews or prints a server file (QA-D01).
 *
 * Never put an `/api/v1/...` URL in an `<a href>` or `window.open`: a top-level navigation / new
 * tab carries no Origin and often no Referer, so Sanctum does not treat it as a session request
 * and the user sees a 401 (and localhost vs 127.0.0.1 are different cookie hosts anyway).
 *
 *  - downloadFile(path)  → XHR with credentials → blob → saved under the server's filename.
 *  - openFile(target)    → asks the API for a 60 s single-use signed link and opens it in a new
 *                          tab (works with no cookie/Referer; the API re-checks access).
 *  - saveBlob(blob,name) → save bytes the page already holds (client-built CSV etc.).
 *  - useFileActions()    → the same, with progress / success / error toasts.
 */

type Params = Record<string, string | number | boolean | undefined | null>

/** A stored_files id, or any allow-listed file GET path (PDF / CSV / XLSX / …/download). */
export type FileTarget = { fileId: string } | { path: string; params?: Params }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * An attachment reference stored on a record (claim receipt, tax proof, onboarding upload):
 * a stored_files id → a signed target; an absolute http(s) URL → used as is; else nothing.
 */
export function fileTarget(ref: string | null | undefined): FileTarget | { external: string } | null {
  if (!ref) return null
  if (UUID.test(ref)) return { fileId: ref }
  if (/^https?:\/\//i.test(ref)) return { external: ref }
  return null
}

function cleanParams(params?: Params): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') out[k] = v
  return out
}

function withQuery(path: string, params?: Params): string {
  const p = cleanParams(params)
  if (!p || Object.keys(p).length === 0) return path
  const qs = new URLSearchParams(Object.entries(p).map(([k, v]) => [k, String(v)])).toString()
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`
}

/** filename / filename* from a Content-Disposition header. */
export function filenameFromDisposition(disposition: string | undefined | null, fallback: string): string {
  const d = disposition ?? ''
  const star = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(d)
  if (star) { try { return decodeURIComponent(star[1]) } catch { return star[1] } }
  const plain = /filename="?([^";]+)"?/i.exec(d)
  return plain ? plain[1] : fallback
}

/** Save bytes as a file. The object URL is revoked after the browser has taken it. */
export function saveBlob(blob: Blob, filename: string): void {
  // Android app: a WebView ignores <a download>; save to Downloads natively instead (and open it).
  if (isNativeApp()) {
    void nativeSave(blob, filename).catch((e) => console.error('[download] native save failed', e))
    return
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/** Android app: save to the public Downloads folder, then open it in the system viewer. */
async function nativeSave(blob: Blob, filename: string): Promise<void> {
  const n = await import('@/mobile/native')
  const saved = await n.nativeSaveBlob(blob, filename)
  const r = await n.BigroiNative.openFile({ uri: saved.uri, mimeType: saved.mimeType })
  if (!r.opened) await n.BigroiNative.shareFile({ uri: saved.uri, mimeType: saved.mimeType, title: filename })
}

/** Fetch a server file as a blob; a JSON body on a blob request is an error envelope → throws. */
export async function fetchFileBlob(path: string, params?: Params): Promise<{ blob: Blob; filename: string | null }> {
  const res = await api.get(path, { params: cleanParams(params), responseType: 'blob' })
  const blob = res.data as Blob
  if (blob?.type?.includes('application/json')) {
    let message = 'The server did not return a file.'
    try { message = (JSON.parse(await blob.text()) as { error?: { message?: string } }).error?.message ?? message } catch { /* keep */ }
    throw new Error(message)
  }
  if (!blob || blob.size === 0) throw new Error('The server returned an empty file.')
  const disposition = (res.headers as Record<string, string | undefined> | undefined)?.['content-disposition']
  const name = filenameFromDisposition(disposition, '')
  return { blob, filename: name || null }
}

/** Download a server file (exports, templates, CSV/XLSX/PDF). Resolves to the saved filename. */
export async function downloadFile(path: string, opts: { params?: Params; filename?: string } = {}): Promise<string> {
  const { blob, filename } = await fetchFileBlob(path, opts.params)
  const fallback = opts.filename ?? (path.split('?')[0].split('/').pop() || 'download')
  const name = filename ?? fallback
  if (isNativeApp()) {
    await nativeSave(blob, name)   // errors reach the caller's toast
    return name
  }
  saveBlob(blob, name)
  return name
}

/** A 60 s, single-use, user-bound URL that opens the file with no session cookie or Referer. */
export async function signedFileUrl(target: FileTarget): Promise<string> {
  const res = 'fileId' in target
    ? await api.post(`/api/v1/files/${encodeURIComponent(target.fileId)}/link`)
    : await api.post('/api/v1/signed-links', { path: withQuery(target.path, target.params) })
  const url = (res.data as { url?: string })?.url
  if (!url) throw new Error('Could not prepare the file link.')
  return new URL(/^https?:\/\//i.test(url) ? url : `${API_BASE_URL}${url}`, window.location.origin).href
}

/**
 * Open a file in a new tab (view / print / PDF preview). The tab is opened synchronously inside
 * the click so popup blockers allow it, then pointed at the signed URL. If the browser blocks the
 * tab, the file is downloaded instead so the click is never a dead end.
 */
export async function openFile(target: FileTarget | { external: string }): Promise<'opened' | 'downloaded'> {
  if ('external' in target) {
    window.open(target.external, '_blank', 'noopener,noreferrer')
    return 'opened'
  }
  // Android app: no tabs. Fetch with the session and hand the file to the system viewer.
  if (isNativeApp()) {
    const path = 'fileId' in target ? `/api/v1/files/${encodeURIComponent(target.fileId)}` : target.path
    const { blob, filename } = await fetchFileBlob(path, 'fileId' in target ? undefined : target.params)
    const n = await import('@/mobile/native')
    await n.nativeOpenBlob(blob, filename ?? (path.split('?')[0].split('/').pop() || 'document'))
    return 'opened'
  }
  const win = window.open('', '_blank')
  if (win) { try { win.opener = null } catch { /* cross-origin guard */ } }
  try {
    const url = await signedFileUrl(target)
    if (win) { win.location.replace(url); return 'opened' }
  } catch (e) {
    win?.close()
    throw e
  }
  if ('fileId' in target) await downloadFile(`/api/v1/files/${encodeURIComponent(target.fileId)}`)
  else await downloadFile(target.path, { params: target.params })
  return 'downloaded'
}

/** The helpers with progress / success / error toasts. */
export function useFileActions() {
  const toast = useToast()
  return useMemo(() => ({
    download: async (path: string, opts: { params?: Params; filename?: string; label?: string } = {}): Promise<boolean> => {
      toast.info(`Preparing ${opts.label ?? 'download'}…`, { duration: 2000 })
      try {
        const name = await downloadFile(path, opts)
        toast.success(`Downloaded ${name}.`)
        return true
      } catch (e) {
        toast.error(errorMessage(e), { title: 'Download failed' })
        return false
      }
    },
    open: async (target: FileTarget | { external: string } | null): Promise<boolean> => {
      if (!target) { toast.error('This file is not available.'); return false }
      try {
        const how = await openFile(target)
        if (how === 'downloaded') toast.info('Your browser blocked the new tab, so the file was downloaded instead.')
        return true
      } catch (e) {
        toast.error(errorMessage(e), { title: 'Could not open the file' })
        return false
      }
    },
  }), [toast])
}
